import type { NavigateFunction } from 'react-router-dom';
import type { ContextualSuggestion } from '../services/contextualIntelligence';
import type { Insight } from '../services/tabs';
import { skillService } from '../services/skills';

export async function executeContextualSuggestion(
  suggestion: ContextualSuggestion,
  navigate: NavigateFunction,
): Promise<string | null> {
  const title = suggestion.title.toLowerCase();
  const tags = suggestion.tags.map((t) => t.toLowerCase());

  if (title.includes('group') && title.includes('tab')) {
    try {
      await skillService.executeSkill('group_by_domain', {});
    } catch {
      // Navigation still helps the user finish grouping manually.
    }
    navigate('/tabs?section=groups');
    return 'Tab grouping started';
  }

  if (title.includes('declutter') || tags.includes('focus')) {
    navigate('/productivity?section=focus');
    return 'Opened focus mode';
  }

  if (title.includes('break') || tags.includes('health')) {
    navigate('/productivity?section=pomodoro');
    return 'Opened break timer';
  }

  if (
    title.includes('synthesize') ||
    title.includes('research') ||
    tags.includes('synthesis') ||
    suggestion.type === 'resource'
  ) {
    navigate('/apps/synthesize');
    return 'Opened research synthesis';
  }

  if (tags.includes('organization')) {
    try {
      await skillService.executeSkill('auto_organize', {});
    } catch {
      // ignore
    }
    navigate('/tabs');
    return 'Workspace organization started';
  }

  return null;
}

export function navigateForInsight(insight: Insight, navigate: NavigateFunction): string {
  const title = insight.title.toLowerCase();

  if (insight.type === 'pattern' || title.includes('cluster') || title.includes('activity')) {
    navigate('/ai?section=patterns');
    return 'Opened patterns';
  }
  if (title.includes('synthesize') || title.includes('research') || title.includes('topics')) {
    navigate('/apps/synthesize');
    return 'Opened research synthesis';
  }
  if (title.includes('crowded') || title.includes('declutter')) {
    navigate('/productivity?section=focus');
    return 'Opened focus mode';
  }
  if (insight.type === 'warning' || title.includes('distraction') || title.includes('block')) {
    navigate('/productivity?section=focus');
    return 'Opened distraction blocking';
  }
  if (title.includes('focus') || title.includes('declutter')) {
    navigate('/productivity?section=focus');
    return 'Opened focus mode';
  }
  if (title.includes('group') || title.includes('organize')) {
    navigate('/tabs?section=groups');
    return 'Opened tab groups';
  }
  if (title.includes('predict') || title.includes('preload')) {
    navigate('/tabs?section=predict');
    return 'Opened tab predictions';
  }
  if (insight.type === 'opportunity') {
    navigate('/apps/synthesize');
    return 'Opened research synthesis';
  }
  navigate('/suggestions');
  return 'Opened smart suggestions';
}
